// scheduling/pages/JobPools.jsx
// The Job Pools page — /schedule/pool (Trello #257). Nav label: "Job Pools".
//
// Two-panel layout borrowed from the POC: pool queues on the right, the viewer's
// own pool context on the left. Built from RPRS's existing classes, not the POC's
// inline styles.
//
// What this page is: the claim side of the pool, plus a manager's assign path.
// Availability check-in + the capacity signal land in the next gate; the assign
// modal already reads whatever check-ins exist so it works either way (via "Push
// to Anyone" when nobody is checked in).
//
// All three pool tabs are always visible — ineligible ones are grayed and
// disabled, with a tooltip saying why, so the structure of the system stays
// legible even to someone who can only claim from one pool.

import { useCallback, useEffect, useMemo, useState } from 'react';
import { useOutletContext } from 'react-router-dom';
import { useAuth } from '../../auth/AuthContext';
import { useUrlState } from '../../hooks/useUrlState';
import { POOL_CODES, POOL_META, ineligibleReason, buildPoolRules, difficultyBand, difficultyLabel } from '../utils/poolEligibility';
import {
  fetchPools, fetchMyPoolHistory, fetchPoolAvailability,
  claimPoolJob, assignPoolJob,
} from '../services/poolApi';
import { fetchStaff, fetchNotifications } from '../services/schedData';
import { markNotifRead } from '../services/schedApi';
import PoolJobCard from '../components/pool/PoolJobCard';
import ClaimConfirmModal from '../components/pool/ClaimConfirmModal';
import AssignModal from '../components/pool/AssignModal';
import MyPoolHistory from '../components/pool/MyPoolHistory';
import CheckInWidget from '../components/pool/CheckInWidget';
import AvailableStaffPanel from '../components/pool/AvailableStaffPanel';
import AssignmentAckPanel from '../components/pool/AssignmentAckPanel';
import SendJobSearchModal from '../components/pool/SendJobSearchModal';
import SendToPoolModal from '../components/pool/SendToPoolModal';

export default function JobPools() {
  const { isSuperUser } = useAuth();
  const { refreshNotifications, poolAccess } = useOutletContext() || {};

  const [data, setData] = useState(null);          // rprsPool?action=list
  const [history, setHistory] = useState(null);
  const [staff, setStaff] = useState([]);
  const [available, setAvailable] = useState([]);
  const [availLoading, setAvailLoading] = useState(true);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [toast, setToast] = useState(null);

  // Unread pool_assignment notices drive the acknowledgment panel. Fetched here
  // (rather than read from AppShell) so the panel appears on first open of THIS
  // page, which is where the job details and the PE link belong.
  const [ackNotifs, setAckNotifs] = useState([]);
  const [ackDismissed, setAckDismissed] = useState(false);

  // Claim / assign flow state. `pendingAssign` holds the job while the manager
  // picks a person; `confirm` is the final gate for either path.
  const [pendingAssign, setPendingAssign] = useState(null);
  const [confirm, setConfirm] = useState(null);     // { job, assignee? }
  const [submitting, setSubmitting] = useState(false);
  const [actionError, setActionError] = useState(null);

  // "Send a job" — search first, then the usual Send to Pool modal for the pick.
  const [sendSearchOpen, setSendSearchOpen] = useState(false);
  const [sendJob, setSendJob] = useState(null);

  const [activePool, setActivePool] = useUrlState('pool', 'GEN', { validate: POOL_CODES });

  const load = useCallback(async () => {
    setError(null);
    try {
      // Availability is loaded unfiltered (no forPool) so the manager panel shows
      // everyone who's free; per-pool filtering happens in the assign modal.
      const [pools, hist, av] = await Promise.all([
        fetchPools(),
        fetchMyPoolHistory(15),
        fetchPoolAvailability().catch(() => ({ available: [] })),
      ]);
      setData(pools);
      setHistory(hist.jobs || []);
      setAvailable(av.available || []);
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
      setAvailLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  // Unread pool_assignment notices for the ack panel. Keyed on the resolved staff
  // id because schedData's notifications read requires an explicit staffId (it
  // returns [] without one) — so this can only run once `list` has told us who we
  // are.
  const meStaffId = data?.me?.StaffId || null;
  useEffect(() => {
    if (!meStaffId) return;
    let alive = true;
    fetchNotifications(meStaffId, { unreadOnly: true })
      .then(rows => {
        if (!alive) return;
        const list = Array.isArray(rows) ? rows : [];
        setAckNotifs(list.filter(n => n.NotifType === 'pool_assignment' && !n.IsRead));
      })
      .catch(() => { /* the panel is a nicety — never block the page */ });
    return () => { alive = false; };
  }, [meStaffId]);

  // Marking them read is what clears the nav pulse, mirroring dismissBugPulse.
  const dismissAck = useCallback(async () => {
    setAckDismissed(true);
    const ids = ackNotifs.map(n => n.NotifId);
    await Promise.all(ids.map(id => markNotifRead(id).catch(() => {})));
    refreshNotifications?.();
  }, [ackNotifs, refreshNotifications]);

  // Assign-modal data, kept SEPARATE from `available`: the modal wants only staff
  // eligible for that one pool, while the manager panel shows everyone free. Sharing
  // one state would let opening the modal silently narrow the panel behind it.
  const [assignAvailable, setAssignAvailable] = useState([]);
  const ensureAssignData = useCallback(async (poolCode) => {
    const [av, st] = await Promise.all([
      fetchPoolAvailability(poolCode).catch(() => ({ available: [] })),
      staff.length ? Promise.resolve(null) : fetchStaff().catch(() => []),
    ]);
    setAssignAvailable(av.available || []);
    if (!staff.length && Array.isArray(st)) setStaff(st);
  }, [staff.length]);

  const rules = useMemo(() => (data?.rules ? data.rules : buildPoolRules(null)), [data]);
  // Memoized because the `|| []` fallback would otherwise be a fresh array each
  // render, invalidating every useMemo downstream that depends on it.
  const myPools = useMemo(() => data?.myPools || [], [data]);
  const me = data?.me || null;

  // "This is mine to act on" — your job (manager or partner), or you're the one who
  // released it, or you're an admin. Gates both Assign and note editing, which need the
  // same answer; it was inline in the card props twice before.
  const canManageJob = useCallback((job) => (
    isSuperUser
    || job.PooledByStaffId === me?.StaffId
    || job.ManagerId === me?.StaffId
    || job.PartnerId === me?.StaffId
  ), [isSuperUser, me]);

  // Patch the edited note into local state instead of refetching the whole pool: a
  // refetch would re-sort the queue mid-interaction and could move the card the user
  // just edited out from under them.
  const patchJobNotes = useCallback((poolJobId, notes) => {
    setData(d => (d ? {
      ...d,
      pools: Object.fromEntries(Object.entries(d.pools || {}).map(([code, jobs]) => [
        code, (jobs || []).map(j => (j.PoolJobId === poolJobId ? { ...j, Notes: notes } : j)),
      ])),
    } : d));
  }, []);

  const showToast = (msg) => {
    setToast(msg);
    setTimeout(() => setToast(null), 4000);
  };

  async function doClaim() {
    setSubmitting(true);
    setActionError(null);
    try {
      const res = await claimPoolJob(confirm.job.PoolJobId);
      setConfirm(null);
      const peNote = res.pe?.skipped
        ? ' (PE write skipped — writes are disabled)'
        : res.pe?.state === 'failed' ? ' — but the PE write FAILED, tell an admin' : '';
      showToast(`You claimed ${confirm.job.ClientName}${peNote}`);
      await load();
    } catch (e) {
      // 409 covers both "someone beat you to it" and "that isn't the head of the
      // queue" — reload so the list reflects reality either way.
      setActionError(e.message);
      if (e.status === 409) await load();
    } finally {
      setSubmitting(false);
    }
  }

  async function doAssign() {
    setSubmitting(true);
    setActionError(null);
    try {
      const res = await assignPoolJob({
        poolJobId: confirm.job.PoolJobId,
        staffId: confirm.assignee.StaffId,
      });
      setConfirm(null);
      const rankNote = res.fifoRankAtAssign && res.fifoRankAtAssign > 1
        ? ` (queue position ${res.fifoRankAtAssign} — recorded for the FIFO audit)`
        : '';
      const peNote = res.pe?.skipped ? ' · PE write skipped' : res.pe?.state === 'failed' ? ' · PE write FAILED' : '';
      showToast(`Assigned ${confirm.job.ClientName} to ${confirm.assignee.Name}${rankNote}${peNote}`);
      await load();
    } catch (e) {
      setActionError(e.message);
      if (e.status === 409) await load();
    } finally {
      setSubmitting(false);
    }
  }

  // Memoized so the per-band rank map below isn't rebuilt on every render.
  const poolJobs = useMemo(() => data?.pools?.[activePool] || [], [data, activePool]);
  const counts = data?.counts || Object.fromEntries(POOL_CODES.map(c => [c, 0]));
  const activeEligible = myPools.includes(activePool);

  // Each card's place WITHIN ITS DIFFICULTY BAND. The list stays one oldest-first queue
  // (the server sorts by PooledAt), but claim order is per band, so #1 of each band is
  // the one you can take. Jobs with no difficulty in PE are a band of their own.
  const bandRanks = useMemo(() => {
    const seen = {};
    const ranks = new Map();
    for (const j of poolJobs) {
      const b = difficultyBand(j.Complexity);
      seen[b] = (seen[b] || 0) + 1;
      ranks.set(j.PoolJobId, seen[b]);
    }
    return ranks;
  }, [poolJobs]);

  // Which pool holds the caller back from marking themselves available — only ever
  // the Preparer pool, and only for Gen Track staff (DJB's rule, decided server-side).
  const waitingLabel = useMemo(() => {
    const busy = data?.availabilityBlockedBy || [];
    if (!busy.length) return null;
    return `${busy.map(c => `${POOL_META[c].label} (${data.counts[c]})`).join(', ')} still has work waiting.`;
  }, [data]);

  // Job leads and admins see who's free and can request them. The server decides —
  // every JM gets the 8am available-staff email, so it has to include JMs with nothing
  // in a pool yet, which the page can't tell from the queues alone.
  const canSeeAvailable = !!data?.canSeeAvailable || isSuperUser;
  // Job leads, ADMIN-department staff and admins can send any job from here.
  const canSend = !!data?.canSend || isSuperUser;

  // Pilot gate. The nav link is hidden for everyone outside the group, but the route
  // is still reachable by URL — and every rprsPool action 403s anyway, so without this
  // the page would just render an error. Say what's actually going on instead.
  // `poolAccess` is undefined until AppShell's probe resolves, so only the explicit
  // false is treated as a denial.
  if (poolAccess === false) {
    return (
      <div className="sched-placeholder">
        <h2>Job Pools isn&rsquo;t open yet</h2>
        <p>
          Job Pools is being piloted with a small group before it goes firm-wide. You
          don&rsquo;t have access to it yet — nothing is wrong with your account.
        </p>
      </div>
    );
  }

  if (loading) {
    return <div className="sched-home-loading"><div className="sched-home-loading-text">Loading job pools…</div></div>;
  }
  if (error) {
    return (
      <div className="sched-placeholder">
        <h2>Error Loading Job Pools</h2>
        <p style={{ color: '#DC2626' }}>{error}</p>
      </div>
    );
  }

  return (
    <div className="sched-pool-page">
      {toast && <div className="sched-pool-toast">{toast}</div>}

      {/* Assignment hand-off: the one unmistakable moment telling a staffer they
          were given work while sitting available. Dismissing marks the notices read,
          which clears the nav pulse too. */}
      {!ackDismissed && ackNotifs.length > 0 && (
        <AssignmentAckPanel notifications={ackNotifs} onDismiss={dismissAck} />
      )}

      {/* ── Stats bar ── */}
      <div className="sched-pool-stats">
        <div className="sched-pool-stat">
          <span className="sched-pool-stat-val">{POOL_CODES.reduce((n, c) => n + (counts[c] || 0), 0)}</span>
          <span className="sched-pool-stat-lbl">Jobs<br />waiting</span>
        </div>
        {POOL_CODES.map(code => (
          <div key={code} className="sched-pool-stat">
            <span className="sched-pool-stat-val" style={{ color: POOL_META[code].color }}>{counts[code]}</span>
            <span className="sched-pool-stat-lbl">{POOL_META[code].label.replace(' ', '\n')}</span>
          </div>
        ))}
        <div className="sched-pool-stats-note">Oldest first within each difficulty · jobs under 20h</div>
        {canSend && (
          <button type="button" className="sched-pool-sidebar-btn sched-pool-send-btn"
            onClick={() => setSendSearchOpen(true)}>
            &#8594; Send a job to a pool
          </button>
        )}
      </div>

      <div className="sched-pool-main">
        {/* ── Left: your pool context ── */}
        <aside className="sched-pool-side">
          <div className="sched-pool-side-block">
            <div className="sched-pool-side-title">Your pools</div>
            {myPools.length === 0 ? (
              <div className="sched-pool-side-empty">
                You aren&rsquo;t eligible to claim from any pool.
                {isSuperUser && ' (As an admin you can still assign work from any pool.)'}
              </div>
            ) : (
              myPools.map(code => (
                <div key={code} className="sched-pool-side-pool">
                  <span className="sched-pool-chip" style={{ background: POOL_META[code].color }}>
                    {POOL_META[code].label}
                  </span>
                  <span className="sched-pool-side-blurb">{POOL_META[code].blurb}</span>
                </div>
              ))
            )}
          </div>

          <div className="sched-pool-side-block">
            <div className="sched-pool-side-title">Availability</div>
            <CheckInWidget
              myPools={myPools}
              canGoAvailable={!!data?.canGoAvailable}
              availability={data?.myAvailability || null}
              waitingLabel={waitingLabel}
              onChanged={load}
            />
          </div>

          {/* Manager side of channel 3 — who's free right now. Only shown to people
              who could actually hand out work. */}
          {canSeeAvailable && (
            <div className="sched-pool-side-block">
              <div className="sched-pool-side-title">Available now ({available.length})</div>
              <AvailableStaffPanel
                available={available}
                loading={availLoading}
                // Requesting someone clears their availability, so the panel they were
                // listed in is now stale — reload it rather than leaving a ghost row.
                onRequested={(name) => {
                  showToast(`Requested ${name} — they've been notified and are no longer marked available`);
                  fetchPoolAvailability(activePool)
                    .then(r => setAvailable(r.available || []))
                    .catch(() => {});
                }}
              />
            </div>
          )}

          <div className="sched-pool-side-block">
            <div className="sched-pool-side-title">My recent pool work</div>
            <MyPoolHistory jobs={history} loading={history === null} />
          </div>
        </aside>

        {/* ── Right: the queues ── */}
        <section className="sched-pool-queues">
          <div className="sched-pool-tabs">
            {POOL_CODES.map(code => {
              const eligible = myPools.includes(code);
              // Ineligible tabs stay visible but disabled — the tooltip explains
              // why, so "grayed out" is never a mystery.
              const why = eligible ? null : (me ? ineligibleReason(me, code, rules) : 'Not eligible');
              return (
                <button
                  key={code}
                  type="button"
                  className={`sched-pool-tab${activePool === code ? ' sched-pool-tab--on' : ''}${eligible ? '' : ' sched-pool-tab--ineligible'}`}
                  onClick={() => setActivePool(code)}
                  title={why || `${POOL_META[code].label} — ${POOL_META[code].blurb}`}
                >
                  <span className="sched-pool-tab-label">{POOL_META[code].label}</span>
                  <span className="sched-pool-tab-count" style={activePool === code ? { background: POOL_META[code].color, color: '#fff' } : undefined}>
                    {counts[code]}
                  </span>
                  {!eligible && <span className="sched-pool-tab-lock" aria-label="not eligible">🔒</span>}
                </button>
              );
            })}
          </div>

          {!activeEligible && (
            <div className="sched-pool-ineligible-note">
              {me ? ineligibleReason(me, activePool, rules) : 'You aren’t eligible for this pool'}
              {isSuperUser ? ' — you can still assign this work as an admin.' : ' — you can view it, but not claim from it.'}
            </div>
          )}

          <div className="sched-pool-list">
            {poolJobs.length === 0 ? (
              <div className="sched-pool-empty">
                <div className="sched-pool-empty-icon">✓</div>
                <div className="sched-pool-empty-title">{POOL_META[activePool].label} pool is empty</div>
                <div className="sched-pool-empty-sub">Nothing waiting to be claimed.</div>
              </div>
            ) : (
              poolJobs.map((job) => (
                <PoolJobCard
                  key={job.PoolJobId}
                  job={job}
                  rank={bandRanks.get(job.PoolJobId)}
                  canClaim={activeEligible}
                  canAssign={canManageJob(job)}
                  // Same permission as assigning: your job, or you pooled it, or admin.
                  // Cards here are always 'waiting' (the queue only lists those), so no
                  // extra state check is needed — the server enforces it regardless.
                  canEditNotes={canManageJob(job)}
                  onNotesSaved={patchJobNotes}
                  claimBlockedReason={bandRanks.get(job.PoolJobId) > 1
                    ? (difficultyBand(job.Complexity)
                      ? `Oldest first within each difficulty — claim the oldest ${difficultyLabel(job.Complexity)} job first`
                      : 'Oldest first within each difficulty — claim the oldest job with no difficulty set first')
                    : null}
                  busy={submitting}
                  onClaim={(j) => { setActionError(null); setConfirm({ job: j }); }}
                  onAssign={async (j) => {
                    setActionError(null);
                    await ensureAssignData(j.PoolCode);
                    setPendingAssign(j);
                  }}
                />
              ))
            )}
          </div>
        </section>
      </div>

      {pendingAssign && (
        <AssignModal
          job={pendingAssign}
          available={assignAvailable}
          staff={staff}
          rules={rules}
          onClose={() => setPendingAssign(null)}
          onPick={(s) => {
            setPendingAssign(null);
            setConfirm({ job: pendingAssign, assignee: s });
          }}
        />
      )}

      {sendSearchOpen && (
        <SendJobSearchModal
          onClose={() => setSendSearchOpen(false)}
          onPick={(j) => { setSendSearchOpen(false); setSendJob(j); }}
        />
      )}

      {sendJob && (
        <SendToPoolModal
          job={sendJob}
          onClose={() => setSendJob(null)}
          onSent={(res) => {
            const peNote = res.pe?.refused ? ` — but PE refused it: ${res.pe.refused}`
              : res.pe?.state === 'failed' ? ' — but the PE write FAILED, tell an admin' : '';
            showToast(`Sent ${sendJob.ClientName} to the ${POOL_META[res.poolCode]?.label || res.poolCode} pool${peNote}`);
            setActivePool(res.poolCode);
            load();
          }}
        />
      )}

      {confirm && (
        <ClaimConfirmModal
          job={confirm.job}
          assignee={confirm.assignee}
          submitting={submitting}
          error={actionError}
          onClose={() => { setConfirm(null); setActionError(null); }}
          onConfirm={confirm.assignee ? doAssign : doClaim}
        />
      )}
    </div>
  );
}
